/**
 * TanStack Query Provider
 *
 * Configures and provides TanStack Query client for the application.
 * Handles server state management, caching, and optimistic updates.
 */

'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState } from 'react';

interface QueryProviderProps {
  children: React.ReactNode;
}

/**
 * Create a query client with optimized defaults for TaskLoops
 */
function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time - how long data stays fresh
        staleTime: 30 * 1000, // 30 seconds
        // Refetch on window focus for real-time feel
        refetchOnWindowFocus: true,
        // Retry failed requests
        retry: (failureCount, error: any) => {
          // Don't retry 4xx errors (client errors)
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          return failureCount < 3;
        },
        // Background refetch interval for live data
        refetchInterval: 5 * 60 * 1000, // 5 minutes
      },
      mutations: {
        // Retry mutations on network errors
        retry: (failureCount, error: any) => {
          // Only retry network errors, not application errors
          if (error?.code === 'NETWORK_ERROR') {
            return failureCount < 2;
          }
          return false;
        },
      },
    },
  });
}

export function QueryProvider({ children }: QueryProviderProps) {
  // Create query client only once per component tree
  const [queryClient] = useState(() => createQueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Dev tools for development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom"
        />
      )}
    </QueryClientProvider>
  );
}